import { useCallback, useMemo, useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';

const KEY_PREFIX = 'scio3:broadcast-banner-dismissed:';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / storage disabled — the banner simply comes back on the next visit.
  }
}

/** Drop every stored dismissal — called from `fullLogout` so a new sign-in starts clean. */
export function clearBroadcastBannerDismissals(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(KEY_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

/**
 * "Closed, but it comes back on the next login" for the Screens-page Broadcast banner.
 *
 * The dismissal stores the Firebase `lastSignInTime` under the user's uid. That timestamp changes
 * on every real sign-in and stays put across token refreshes, so the banner stays hidden through
 * reloads and new tabs of the SAME session and reappears exactly after the next login — without a
 * server-side setting (the Welcome screen's `isShowWelcomeTips` is permanent, which is the wrong
 * shape here).
 */
export function useBroadcastBannerDismissal(): { dismissed: boolean; dismiss: () => void } {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const uid = firebaseUser?.uid ?? 'anonymous';
  const loginMark = firebaseUser?.metadata?.lastSignInTime ?? 'unknown';
  const key = `${KEY_PREFIX}${uid}`;
  const [tick, setTick] = useState(0);

  // `tick` re-reads storage after a dismiss in this render tree; uid/loginMark changes re-read it too.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- tick is a deliberate re-read trigger, not a value the memo reads
  const dismissed = useMemo(() => read(key) === loginMark, [key, loginMark, tick]);

  const dismiss = useCallback(() => {
    write(key, loginMark);
    setTick((n) => n + 1);
  }, [key, loginMark]);

  return { dismissed, dismiss };
}
